"use client";

/* ————— fol. 4r — Readers —————
   A typographic triptych, styled as three rubricated entries in a
   manuscript index — not three identical cards. Left column: hanging
   gold numerals (i. ii. iii.) against big EB Garamond names. Right
   column (desktop only): a quiet oversized ghost numeral + a single
   Instrument Serif display line, so the composition reads as an
   asymmetric spread rather than a grid.
*/

import { Scrub } from "./Scrub";

type Entry = {
  numeral: string;
  name: string;
  description: string;
  tag: string;
};

const ENTRIES: Entry[] = [
  {
    numeral: "i.",
    name: "New believers",
    description:
      "Plain-language context for every passage, with no assumed vocabulary and no guilt about what you don’t know yet.",
    tag: "Guided devotionals",
  },
  {
    numeral: "ii.",
    name: "Growing Christians",
    description: "Original-language tools and cross-references for when a verse won’t let you go.",
    tag: "Word studies",
  },
  {
    numeral: "iii.",
    name: "Bible teachers",
    description: "Study notes and citations you can actually put in front of a class.",
    tag: "Cited study notes",
  },
];

function Entry({ entry, index }: { entry: Entry; index: number }) {
  return (
    <Scrub from="up" className={index > 0 ? "pt-8 sm:pt-10 border-t border-border" : "pt-0"}>
      <div className="group flex items-start gap-5 sm:gap-8">
        <span
          aria-hidden="true"
          className="font-scripture italic shrink-0 leading-none"
          style={{ fontSize: "clamp(1.5rem, 1.1rem + 1.2vw, 2rem)", color: "var(--accent)", marginTop: "0.1em" }}
        >
          {entry.numeral}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="relative inline-block font-display text-3xl sm:text-4xl mb-3 text-balance">
            {entry.name}
            <span
              aria-hidden="true"
              className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-accent transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100"
            />
          </h3>
          <p className="text-muted-foreground leading-relaxed max-w-md mb-3">{entry.description}</p>
          <p className="rubric !text-[0.65rem]">Starts with &middot; {entry.tag}</p>
        </div>
      </div>
    </Scrub>
  );
}

export default function Readers() {
  return (
    <section data-folio="fol. 4r" data-folio-title="Who it is for" className="relative py-28 sm:py-36">
      <div className="container">
        <Scrub from="up" className="max-w-2xl mb-16 sm:mb-20">
          <p className="rubric mb-4">Who it&rsquo;s for</p>
          <h2 className="text-balance">Three ways to read. One apparatus underneath.</h2>
        </Scrub>

        <div className="grid gap-14 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-16 items-stretch">
          <div className="flex flex-col">
            {ENTRIES.map((entry, i) => (
              <Entry key={entry.name} entry={entry} index={i} />
            ))}
          </div>

          {/* Quiet right column — desktop only. Ghost numeral + one display line. */}
          <Scrub from="up" className="hidden lg:flex flex-col justify-between border-l border-border pl-10 xl:pl-14">
            <p aria-hidden="true" className="numeral-outline text-[9rem] xl:text-[11rem]">
              IV
            </p>
            <p className="font-display text-2xl xl:text-3xl text-muted-foreground leading-snug text-balance max-w-xs">
              Meets you wherever you&rsquo;re reading from.
            </p>
          </Scrub>
        </div>
      </div>
    </section>
  );
}
